import { Calendar, CalendarDays, List, Plus } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import MeetingListItem from '../components/meetings/MeetingListItem';
import MeetingCalendar from '../components/meetings/MeetingCalendar';
import MeetingErrorBoundary from '../components/meetings/MeetingErrorBoundary';
import { useMeetingDrawer } from '../hooks/useMeetingDrawer';
import { useToast } from '../contexts/ToastContext';
import meetingService from '../services/meetingService';
import { Meeting, MeetingListResponse } from '../types';

type ViewType = 'list' | 'calendar';

// Unused interfaces - kept for future enhancement
// interface LocalMeetingFilters {
//   search?: string;
//   status?: string;
//   visibility?: string;
//   categoryId?: string;
//   tags?: string[];
//   dateRange?: {
//     from?: string;
//     to?: string;
//   };
// }

interface MeetingsProps {
  projectId?: string;
}

const Meetings: React.FC<MeetingsProps> = ({ projectId: propProjectId }) => {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const projectId = propProjectId || urlProjectId;
  // const location = useLocation(); // Unused - kept for future enhancement
  const { success, error } = useToast();
  const { openMeetingDrawer } = useMeetingDrawer();

  // State management
  const [loading, setLoading] = useState(false);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [totalMeetings, setTotalMeetings] = useState(0);
  const [currentPage] = useState(1); // Kept for future pagination

  // View states
  const [currentView, setCurrentView] = useState<ViewType>('calendar');
  // const [calendarView, setCalendarView] = useState<CalendarViewType>('month');
  // const [selectedDate, setSelectedDate] = useState(new Date());

  // Filter states - unused but kept for future enhancement
  // const [filters, setFilters] = useState<LocalMeetingFilters>({});
  // const [showFilters, setShowFilters] = useState(false);

  // Modal/Drawer states - now handled by context
  // const [showMeetingDrawer, setShowMeetingDrawer] = useState(false);
  // const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  // const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');

  // Load meetings
  const loadMeetings = useCallback(
    async (showLoader = true) => {
      try {
        if (showLoader) {
          setLoading(true);
        }

        // Load meeting list
        const params: any = {
          page: currentPage,
          view: 'list',
        };

        const result = await meetingService.getMeetings(projectId || undefined, params);

        if (result.success && result.data) {
          const response = result.data as MeetingListResponse;
          setMeetings(response.meetings || []);
          setTotalMeetings(response.pagination?.total || 0);
        } else {
          console.error('Meetings list error:', result);
          error(result.message || 'Failed to load meetings');
        }
      } catch (err) {
        console.error('Error loading meetings:', err);
        error('Failed to load meetings');
      } finally {
        setLoading(false);
      }
    },
    [currentPage, error, projectId]
  );

  // Load meetings on component mount and when dependencies change (only for list view)
  useEffect(() => {
    if (currentView === 'list') {
      loadMeetings();
    }
  }, [loadMeetings, currentView]);

  // Event handlers
  const handleCreateMeeting = () => {
    openMeetingDrawer({
      mode: 'create',
      projectId: projectId,
      onSave: () => {
        loadMeetings(false);
        success('Meeting saved successfully');
      },
      onDelete: () => {
        loadMeetings(false);
        success('Meeting deleted successfully');
      },
    });
  };

  const handleEditMeeting = (meeting: Meeting) => {
    openMeetingDrawer({
      mode: 'edit',
      meeting: meeting,
      projectId: projectId,
      onSave: () => {
        loadMeetings(false);
        success('Meeting updated successfully');
      },
      onDelete: () => {
        loadMeetings(false);
        success('Meeting deleted successfully');
      },
    });
  };

  const handleViewMeeting = (meeting: Meeting) => {
    openMeetingDrawer({
      mode: 'view',
      meeting: meeting,
      projectId: projectId,
    });
  };

  return (
    <MeetingErrorBoundary>
      <div className="absolute inset-0 flex flex-col">
        {/* Compact Header Bar */}
        <div className="flex-none bg-white border-b border-gray-200">
          <div className="px-5 py-2.5">
            <div className="flex items-center justify-between">
              {/* Left: Title + Inline Stats */}
              <div className="flex items-center space-x-5">
                <div className="flex items-center space-x-2.5">
                  <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center flex-none">
                    <Calendar className="h-4 w-4 text-brand-600" />
                  </div>
                  <div>
                    <h1 className="text-base font-semibold text-gray-900 leading-tight">
                      {projectId ? 'Meetings' : 'All Meetings'}
                    </h1>
                    <p className="text-[11px] text-gray-500 leading-tight">
                      {projectId ? 'Project meetings' : 'Schedule and manage meetings'}
                    </p>
                  </div>
                </div>

                {/* Inline Stats - Compact */}
                {!loading && meetings.length > 0 && (
                  <div className="hidden lg:flex items-center space-x-3 pl-5 border-l border-gray-200">
                    <div className="flex items-center space-x-1">
                      <span className="text-xs font-semibold text-gray-900">{totalMeetings}</span>
                      <span className="text-[10px] text-gray-400">Total</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Right: Actions */}
              <div className="flex items-center space-x-2">
                {/* View Switcher - Compact */}
                <div className="flex items-center bg-gray-100 rounded-md p-0.5">
                  <button
                    onClick={() => setCurrentView('list')}
                    className={`p-1.5 rounded transition-colors ${
                      currentView === 'list'
                        ? 'bg-white text-brand-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title="List View"
                  >
                    <List className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setCurrentView('calendar')}
                    className={`p-1.5 rounded transition-colors ${
                      currentView === 'calendar'
                        ? 'bg-white text-brand-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title="Calendar View"
                  >
                    <CalendarDays className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Create Meeting Button */}
                <button
                  onClick={handleCreateMeeting}
                  className="inline-flex items-center px-3 py-1.5 text-[11px] font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md transition-colors"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  New Meeting
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-h-0 p-4 bg-gray-50">
          <div className="h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 overflow-auto">
              {currentView === 'calendar' ? (
                <MeetingCalendar projectId={projectId} />
              ) : (
                // List View
                <>
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600 mx-auto"></div>
                        <p className="mt-3 text-xs text-gray-500">Loading meetings...</p>
                      </div>
                    </div>
                  ) : meetings.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-gray-400" />
                      </div>
                      <h3 className="mt-3 text-sm font-medium text-gray-900">No meetings found</h3>
                      <p className="mt-1 text-xs text-gray-500 max-w-sm mx-auto">
                        {projectId
                          ? "This project doesn't have any meetings yet."
                          : "You don't have any meetings scheduled."}
                      </p>
                      <div className="mt-4">
                        <button
                          onClick={handleCreateMeeting}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md transition-colors"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          Create Meeting
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {meetings.map(meeting => (
                        <MeetingListItem
                          key={meeting._id}
                          meeting={meeting}
                          onEdit={() => handleEditMeeting(meeting)}
                          onView={() => handleViewMeeting(meeting)}
                          onDelete={() => {
                            loadMeetings(false);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </MeetingErrorBoundary>
  );
};

export default Meetings;
